import { useEffect, useState } from "react";
import { AGENDA } from "@/data/eventData";
import { X, Users, Mic2, ListChecks, Info } from "lucide-react";

function SessionModal({ session, onClose }) {
  useEffect(() => {
    if (!session) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [session, onClose]);

  if (!session) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="session-modal"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-lg border border-cyan-900/60 bg-[#0B1120] shadow-[0_20px_60px_-20px_rgba(0,240,255,0.35)]">
        <button
          type="button"
          onClick={onClose}
          data-testid="session-modal-close"
          className="absolute right-3 top-3 h-9 w-9 inline-flex items-center justify-center rounded-md border border-cyan-500/30 text-cyan-200 hover:bg-cyan-400/10"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6 lg:p-8">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="mono text-[11px] uppercase tracking-widest text-cyan-300">
              {session.time}
            </span>
            {session.type && (
              <span className="mono text-[10px] uppercase tracking-widest text-slate-950 bg-cyan-400 rounded-full px-2 py-0.5">
                {session.type}
              </span>
            )}
          </div>

          <h3 className="font-display mt-4 text-white font-semibold text-xl lg:text-2xl leading-snug">
            {session.title}
          </h3>

          {session.description && (
            <p className="mt-4 text-slate-300 leading-relaxed">
              {session.description}
            </p>
          )}

          {session.speaker && (
            <div className="mt-6 rounded-md border border-cyan-900/50 bg-[#050d1a] p-4">
              <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-cyan-300 mb-2">
                <Mic2 className="w-3.5 h-3.5" />
                Speaker
              </div>
              <div className="text-slate-100">{session.speaker}</div>
            </div>
          )}

          {session.moderator && (
            <div className="mt-4 rounded-md border border-cyan-900/50 bg-[#050d1a] p-4">
              <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-cyan-300 mb-2">
                <Info className="w-3.5 h-3.5" />
                Moderator
              </div>
              <div className="text-slate-100">{session.moderator}</div>
            </div>
          )}

          {session.panelists?.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-cyan-300 mb-3">
                <Users className="w-3.5 h-3.5" />
                Panelists
              </div>
              <ul className="space-y-2">
                {session.panelists.map((p) => (
                  <li
                    key={p}
                    className="text-sm text-slate-300 pl-4 border-l border-cyan-500/40"
                  >
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {session.topics?.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-cyan-300 mb-3">
                <ListChecks className="w-3.5 h-3.5" />
                Topics
              </div>
              <ul className="space-y-2">
                {session.topics.map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm text-slate-300">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-8 pt-4 border-t border-cyan-900/40 flex justify-end">
            <button
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-full bg-cyan-400 text-slate-950 font-semibold px-5 py-2.5 hover:bg-cyan-300 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DayBlock({ day, onOpen }) {
  return (
    <ol className="relative border-l border-cyan-900/50 pl-6 space-y-6">
      {day.sessions.map((s, i) => {
        const hasDetails =
          s.speaker || s.moderator || s.panelists?.length || s.topics?.length || s.description;
        const key = `${s.time}-${s.title}`;
        return (
          <li key={key} className="relative group">
            <span className="absolute -left-[33px] top-2 w-3 h-3 rounded-full bg-cyan-400 shadow-[0_0_10px_#00F0FF] ring-4 ring-[#050d1a]" />
            <button
              type="button"
              onClick={() => hasDetails && onOpen(s)}
              disabled={!hasDetails}
              data-testid={`agenda-session-${i}`}
              className={`w-full text-left rounded-lg border border-cyan-900/40 bg-[#0B1120] p-5 transition-colors ${
                hasDetails
                  ? "hover:border-cyan-400/60 hover:bg-[#0e1a2e] cursor-pointer"
                  : "opacity-90 cursor-default"
              }`}
            >
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="mono text-[11px] uppercase tracking-widest text-cyan-300">
                  {s.time}
                </span>
                {s.type && (
                  <span className="mono text-[10px] uppercase tracking-widest text-cyan-300/70 border border-cyan-500/30 rounded-full px-2 py-0.5">
                    {s.type}
                  </span>
                )}
              </div>
              <h4 className="font-display mt-2 text-white font-semibold text-base sm:text-lg leading-snug">
                {s.title}
              </h4>
              {s.speaker && (
                <p className="mt-2 text-sm text-slate-400 line-clamp-2">
                  {s.speaker}
                </p>
              )}
              {hasDetails && (
                <div className="mt-3 mono text-[10px] uppercase tracking-widest text-cyan-300/80 opacity-0 group-hover:opacity-100 transition-opacity">
                  Click for details →
                </div>
              )}
            </button>
          </li>
        );
      })}
    </ol>
  );
}

export default function Agenda() {
  const [tab, setTab] = useState("day1");
  const [active, setActive] = useState(null);
  const day = AGENDA[tab];

  return (
    <section id="agenda" className="relative py-24 lg:py-32 border-t border-cyan-900/30 bg-[#050d1a]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mono text-[11px] uppercase tracking-[0.24em] text-cyan-300">
          04 · Agenda
        </div>
        <h2 className="font-display mt-4 text-3xl lg:text-4xl font-bold text-white max-w-2xl">
          Two days. Government + industry. Real commitments.
        </h2>

        <div className="mt-10 inline-flex rounded-full border border-cyan-900/60 bg-[#0B1120] p-1">
          {["day1", "day2"].map((d) => (
            <button
              key={d}
              data-testid={`agenda-tab-${d}`}
              onClick={() => setTab(d)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                tab === d
                  ? "bg-cyan-400 text-slate-950"
                  : "text-slate-300 hover:text-cyan-200"
              }`}
            >
              {AGENDA[d].label} · {AGENDA[d].date.split(",")[1]?.trim() || AGENDA[d].date}
            </button>
          ))}
        </div>

        <div className="mt-4 mono text-[11px] uppercase tracking-widest text-slate-400">
          {day.date} · {day.theme}
        </div>

        <div className="mt-8 max-w-4xl">
          <DayBlock day={day} onOpen={setActive} />
        </div>

        <p className="mt-8 text-xs mono uppercase tracking-widest text-slate-500">
          * Agenda indicative — click any session for full details.
        </p>
      </div>

      <SessionModal session={active} onClose={() => setActive(null)} />
    </section>
  );
}
